/**
 * P11.7 — i18n Runtime Guard.
 *
 * Prevents missing translation keys from breaking the UI by:
 *   1. Returning a clearly-marked placeholder ("[missing:my.key]") instead of
 *      blank/undefined output.
 *   2. Recording every missing key encounter in a sessionStorage buffer so
 *      developers can inspect the run-time gap from the I18n Health page.
 *   3. Optionally reporting missing keys to the backend (best-effort) so the
 *      health dashboard reflects what real users hit.
 *
 * Usage (already wired in src/i18n.js):
 *     import { attachRuntimeGuard } from './i18n/runtimeGuard';
 *     i18n.use(LanguageDetector).use(initReactI18next).init({...});
 *     attachRuntimeGuard(i18n);
 */
const BUFFER_KEY = 'i18n_missing_keys';
const MAX_BUFFER = 200;
let reportedKeys = new Set();

function readBuffer() {
  try {
    return JSON.parse(sessionStorage.getItem(BUFFER_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeBuffer(items) {
  try {
    sessionStorage.setItem(BUFFER_KEY, JSON.stringify(items.slice(-MAX_BUFFER)));
  } catch {
    /* sessionStorage full or unavailable — ignore */
  }
}

function recordMissing(lng, ns, key) {
  const signature = `${lng}::${ns}::${key}`;
  if (reportedKeys.has(signature)) return;
  reportedKeys.add(signature);

  const items = readBuffer();
  items.push({
    lng,
    ns,
    key,
    page: typeof window !== 'undefined' ? window.location?.pathname : '',
    ts: new Date().toISOString(),
  });
  writeBuffer(items);

  // Best-effort backend report (no auth, fire-and-forget). The endpoint
  // accepts up to 1 keys per request to throttle abuse.
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    if (base && typeof fetch === 'function') {
      fetch(`${base}/api/i18n-health/report-missing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lng, ns, key }),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* swallow */
  }
}

export function getMissingKeys() {
  return readBuffer();
}

export function clearMissingKeys() {
  reportedKeys = new Set();
  try {
    sessionStorage.removeItem(BUFFER_KEY);
  } catch {
    /* ignore */
  }
}

export function attachRuntimeGuard(i18n) {
  if (!i18n || i18n.__guardAttached) return;
  i18n.__guardAttached = true;

  i18n.on('missingKey', (lngs, ns, key) => {
    const lng = Array.isArray(lngs) ? lngs[0] : lngs;
    recordMissing(lng || 'unknown', ns || 'translation', key);
  });

  // Wrap the .t() function so the UI never renders bare keys like
  // "settings.tabs.smoke_test" — it now becomes "[missing:settings.tabs.smoke_test]".
  const originalT = i18n.t.bind(i18n);
  i18n.t = (key, options) => {
    const value = originalT(key, options);
    if (typeof value === 'string' && value === key && key && key.includes('.')) {
      return `[missing:${key}]`;
    }
    return value;
  };
}

export default attachRuntimeGuard;
